import {
  Button,
  useDisclosure,
  useOutsideClick,
  Box,
  Icon,
} from '@chakra-ui/react';
import React, { useRef, useState, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import DepartmentIcon from '@/assets/icons/blueprint/Department';
import { KText } from '@/ui-library/components/text';
import { useGetAtsDepartmentsQuery } from '@/store/query/departments';
import { KPopover } from '@/ui-library/components/organisms/KPopover/KPopover';
import { FilterWithLocalSearch } from '@/components/ats/filters/components/FilterWithLocalSeacrh';
import { FilterNestedList } from '@/components/ats/filters/components/FilterNestedList';

interface FilterItemType {
  label: string;
  value: string;
}

interface Department {
  id: string;
  name: string;
  parent_id: string | null;
}

interface AtsDeptFunctionProps {
  onSelect: (item: FilterItemType) => void;
  filter?: {
    function: string | null;
  };
}

export const AtsDeptFunction: React.FC<AtsDeptFunctionProps> = ({
  onSelect = () => {},
  filter = {},
}) => {
  const { data: departments = [] } = useGetAtsDepartmentsQuery({});

  const defaultSelected: Department = {
    name: 'All Departments',
    id: 'all',
    parent_id: null,
  };
  const departmentOptions: Department[] = departments;
  const [selected, setSelected] = useState<Department>(defaultSelected);
  const ref = useRef(null);
  const { isOpen, onToggle, onClose } = useDisclosure();

  useEffect(() => {
    if (filter?.function === null) {
      setSelected(defaultSelected);
    }
  }, [filter]); //eslint-disable-line react-hooks/exhaustive-deps

  useOutsideClick({
    ref: ref,
    handler: () => onClose(),
  });

  const handleDepartmentSelect = (dept: Department) => {
    const item = {
      label: dept.name,
      value: dept.id,
    };
    onSelect(item);
    setSelected(dept);
    onClose();
  };

  const triggerButton = (
    <Button
      px="6px"
      height="2rem"
      variant="quaternary"
      bg="primary-50"
      borderColor="transparent"
      textDecoration="none"
      onClick={onToggle}
      borderRadius="6px"
      w="100%"
      _hover={{ textDecoration: 'none' }}
      _focus={{ textDecoration: 'none' }}
      leftIcon={
        <Icon
          as={DepartmentIcon}
          width="16px"
          height="16px"
          color="secondary-700"
        />
      }
    >
      <KText
        size="sm-medium"
        color="secondary-700"
        isTruncated
        enableTooltip={true}
        maxW="500px"
      >
        {selected.name}
      </KText>
      <Icon
        transitionDuration=".5s"
        willChange="transform"
        transform={isOpen ? 'rotateX(180deg)' : 'rotateX(0)'}
        as={ChevronDownIcon}
        width="16px"
        height="16px"
        color="secondary-700"
      />
    </Button>
  );

  const popoverContent = (
    <Box>
      <FilterWithLocalSearch
        list={[
          defaultSelected,
          ...departmentOptions.map((dept) => ({
            id: dept.id,
            name: dept.name,
            parent_id: dept.parent_id,
          })),
        ]}
        valueKey="name"
        idKey="id"
        type="onlyList"
        isMultiple={false}
        placeholder="Search departments"
        label="department"
        filterConfig={{ id: 'department' }}
        activeFilters={[]}
        CustomRenderList={FilterNestedList}
        isCheckbox={false}
        selected={[]}
        onChange={handleDepartmentSelect}
        handleFilterItemClick={(item) => {
          if (item.id === 'all') {
            onSelect({ label: 'All Departments', value: 'all' });
            setSelected(defaultSelected);
          } else {
            handleDepartmentSelect(item);
          }
          onClose();
        }}
      />
    </Box>
  );

  return (
    <Box as="span" ref={ref}>
      <KPopover
        triggerElement={triggerButton}
        showArrow={false}
        showCloseButton={false}
        popoverProps={{
          placement: 'bottom-start',
          isOpen,
          onClose,
        }}
        popoverContentProps={{
          width: '320px',
          maxH: '400px',
          overflowY: 'auto',
          boxShadow: '0px 4px 12px 6px rgb(46 47 48 / 6%)',
        }}
        popoverBodyProps={{
          p: '16px',
        }}
        popoverActions={{
          isOpen,
          onOpen: onToggle,
          onClose,
        }}
      >
        {popoverContent}
      </KPopover>
    </Box>
  );
};
